import { useEffect } from "react";
import { useLang } from "../context/LangContext";
import { SITE, pathFor, PORTRAIT_URL } from "../lib/site";

const upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href, hreflang) => {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const applyBaseMeta = (title, description, lang) => {
  document.documentElement.lang = lang;
  if (title) document.title = title;
  if (description) {
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:description", description);
    upsertMeta("name", "twitter:description", description);
  }
  upsertMeta("property", "og:title", title || document.title);
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:site_name", "Maria João Nepomuceno — Advogada");
  upsertMeta("property", "og:locale", lang === "pt" ? "pt_PT" : "en_US");
  upsertMeta("property", "og:locale:alternate", lang === "pt" ? "en_US" : "pt_PT");
  if (PORTRAIT_URL) {
    upsertMeta("property", "og:image", PORTRAIT_URL);
    upsertMeta("property", "og:image:alt", "Maria João Nepomuceno, Advogada");
    upsertMeta("name", "twitter:image", PORTRAIT_URL);
  }
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", title || document.title);
};

const applyCanonicals = (pageKey, lang) => {
  const canonical = SITE.origin + pathFor(pageKey, lang);
  upsertMeta("property", "og:url", canonical);
  upsertLink("canonical", canonical);
  upsertLink("alternate", SITE.origin + pathFor(pageKey, "pt"), "pt-PT");
  upsertLink("alternate", SITE.origin + pathFor(pageKey, "en"), "en");
  upsertLink("alternate", SITE.origin + pathFor(pageKey, "pt"), "x-default");
};

const SERVICES = {
  pt: ["Divórcio e Separação", "Responsabilidades Parentais", "Pensões de Alimentos", "Direito da Família Internacional", "União de Facto e Património", "Vistos e Residência"],
  en: ["Divorce & Separation", "Parental Responsibilities", "Child & Spousal Support", "International Family Law", "Cohabitation & Assets", "Visas & Residency"],
};

const buildJsonLd = (description, lang) => ({
  "@context": "https://schema.org",
  "@type": ["Attorney", "LegalService"],
  name: "Maria João Nepomuceno — Advogada",
  alternateName: SITE.name,
  description,
  url: SITE.origin,
  image: PORTRAIT_URL || undefined,
  telephone: "+351938718884",
  email: SITE.email,
  priceRange: "$$",
  areaServed: ["PT", "Madeira", "Funchal"],
  address: {
    "@type": "PostalAddress",
    streetAddress: "Rua Ivens 22, 3.º Esq.",
    addressLocality: "Funchal",
    addressRegion: "Madeira",
    postalCode: "9000-046",
    addressCountry: "PT",
  },
  geo: { "@type": "GeoCoordinates", latitude: 32.6493, longitude: -16.9089 },
  sameAs: ["https://www.instagram.com/mariajoaonep/"],
  knowsLanguage: ["pt-PT", "en"],
  founder: {
    "@type": "Person",
    name: SITE.name,
    jobTitle: lang === "pt" ? "Advogada" : "Lawyer",
    identifier: "Cédula OA 200-M",
    parent: {
      "@type": "Person",
      name: "Rui Nepomuceno",
      honorificPrefix: "Comendador",
      description: lang === "pt"
        ? "Advogado, político e historiador da Madeira"
        : "Lawyer, politician and historian of Madeira",
    },
    sibling: {
      "@type": "Person",
      name: "Filipa Nepomuceno",
      jobTitle: lang === "pt"
        ? "Presidente do Conselho Deontológico da Ordem dos Advogados da Madeira"
        : "President of the Deontology Council of the Madeira Bar Association",
    },
  },
  makesOffer: SERVICES[lang].map((n) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: n } })),
});

const applyJsonLd = (description, lang) => {
  const ldId = "ld-attorney";
  let ld = document.getElementById(ldId);
  if (!ld) {
    ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = ldId;
    document.head.appendChild(ld);
  }
  ld.textContent = JSON.stringify(buildJsonLd(description, lang));
};

export const Seo = ({ title, description, pageKey = "home" }) => {
  const { lang } = useLang();
  useEffect(() => {
    applyBaseMeta(title, description, lang);
    applyCanonicals(pageKey, lang);
    applyJsonLd(description, lang);
  }, [title, description, pageKey, lang]);

  return null;
};
